import { Injectable, ViewContainerRef } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { v4 as UUID } from 'uuid';

import { EMPTY, of, Subject } from 'rxjs';

import { finalize, switchMap } from 'rxjs/operators';
import { SiteData } from '../../interfaces/site-data';
import { App, FieldIdentifier, LogicStyledGridRowCellDictionary, MapFieldPoint, MapFieldPointMarkerData, MappedMapElementColorSelection, PointCategory, PresetStyleColorSelection, Project, Site } from '../../models';
import { FieldPointSiteClusterData } from '../../models/fieldpoint-site-cluster-data';
import { PointCategoryCache } from '../../models/point-category-cache';
import { FieldPointIconComponent } from '../../shared/components/field-point-icon/field-point-icon.component';
import { NUMBER_FORMAT_CONSTANTS, SNACKBAR_CONSTANTS } from '../../shared/constants';
import { MAP_LAYER_Z_INDEXES } from '../../shared/constants/map-layer-z-indexes';
import { FieldPointIconsSize, WafPrefix } from '../../shared/enums';
import { MapDataService } from '../data-services';
import { ProjectsService } from '../projects.service';
import { ColorsUtilityService, MapClusterUtilityService } from '../utility-services';
import { HTMLRendererUtilityService } from '../utility-services/html-renderer-utility.service';
import { GridFieldTypes, MapFieldPointDefaultFieldTypes } from './enums/field-types';
import { Column } from './models';
import { MapFieldPointLogicServiceModel } from './models/map/map-field-point-logic-service-model';

@Injectable({
    providedIn: 'root'
})
export class MapFieldPointLogicService {
    private _hideInfoWindowTimeout: NodeJS.Timeout | null = null;
    private _hideInfoWindowDelay = 200;
    private _getMapFieldPointByProjectRequestKeys = new Set<string>();

    private _mapFieldPointReady = new Subject<boolean>();
    private _mapFieldPointRowDataEdited = new Subject<boolean>();
    private _mapFieldPointDataEdited = new Subject<boolean>();
    private _pointCategoryPreviewReady = new Subject<void>();
    private _editingFieldPointCoordinates = new Subject<boolean>();
    private _mapFieldPointsAddedToMap = new Subject<boolean>();
    private _fieldPointPreviewedChanged = new Subject<string>();
    private _viewAllFieldPoints = new Subject<void>();
    private _editFieldPoint = new Subject<void>();
    private _fieldPointModalOpened = new Subject<void>();

    mapFieldPointReady$ = this._mapFieldPointReady.asObservable();
    mapFieldPointRowDataEdited$ = this._mapFieldPointRowDataEdited.asObservable();
    mapFieldPointDataEdited$ = this._mapFieldPointDataEdited.asObservable();
    pointCategoryPreviewReady$ = this._pointCategoryPreviewReady.asObservable();
    editingFieldPointCoordinates$ = this._editingFieldPointCoordinates.asObservable();
    mapFieldPointsAddedToMap$ = this._mapFieldPointsAddedToMap.asObservable();
    fieldPointPreviewedChanged$ = this._fieldPointPreviewedChanged.asObservable();
    viewAllFieldPoints$ = this._viewAllFieldPoints.asObservable();
    editFieldPoint$ = this._editFieldPoint.asObservable();
    fieldPointModalOpened$ = this._fieldPointModalOpened.asObservable();

    model: MapFieldPointLogicServiceModel = new MapFieldPointLogicServiceModel();

    defaultColumns = [
        { order: 1, header: '', type: GridFieldTypes.Text },
        { order: 2, header: '', type: MapFieldPointDefaultFieldTypes.PointCategory },
        { order: 3, header: '', type: MapFieldPointDefaultFieldTypes.Coordinates }
    ];

    get currentProject(): Project {
        return this._projectsService.model.currentProject;
    }

    constructor(
        private _mapClusterUtilityService: MapClusterUtilityService,
        private _mapDataService: MapDataService,
        private _matSnackBar: MatSnackBar,
        private _htmlRendererUtilityService: HTMLRendererUtilityService,
        private _formBuilder: UntypedFormBuilder,
        private _colorsUtilityService: ColorsUtilityService,
        private _projectsService: ProjectsService
    ) { }

    initModel(): void {
        this.model = new MapFieldPointLogicServiceModel();
        this.model.form = new UntypedFormGroup({
            id: new UntypedFormControl(''),
            columns: new UntypedFormArray([]),
            pointCategories: this._formBuilder.array([]),
            enableSubRow: new UntypedFormControl(false)
        });
        this.model.previewPointCategories = [];
        this.setAvailableColors();
    }

    private setAvailableColors(): void {
        this.model.mapFieldPointAvailableColors = [];
        this._colorsUtilityService.model.mapElementColors.forEach((fieldStyleColor: MappedMapElementColorSelection, key: number) => {
            const mapFieldPointFillColor: PresetStyleColorSelection = new PresetStyleColorSelection();
            mapFieldPointFillColor.class = fieldStyleColor.class;
            mapFieldPointFillColor.name = fieldStyleColor.name;
            mapFieldPointFillColor.id = key;
            this.model.mapFieldPointAvailableColors.push(mapFieldPointFillColor);
        });
    }

    createMapFieldPoint(currentApp: App, fieldPoint: MapFieldPoint): void {
        this._mapDataService.createMapFieldPoint(currentApp, fieldPoint).subscribe({
            next: (createdFieldPoint: MapFieldPoint) => {
                this.loadFieldPointToModel(createdFieldPoint);
                this._mapFieldPointReady.next(true);
                this._matSnackBar.open(SNACKBAR_CONSTANTS.FIELD_POINT_ADDED, SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
            },
            error: () => {
                this._matSnackBar.open(SNACKBAR_CONSTANTS.FIELD_POINT_ADD_ERROR, SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
            }
        });
    }

    getMapFieldPointByProject(currentApp: App, project: Project): void {
        if (project?.isSummaryProject) {
            return;
        }

        const requestedProjectId = project?.id;
        const requestKey = `${currentApp?.id}_${project?.id}`;

        if (this._getMapFieldPointByProjectRequestKeys.has(requestKey)) {
            return;
        }

        this._getMapFieldPointByProjectRequestKeys.add(requestKey);

        this._mapDataService.getMapFieldPointByProject(currentApp, project)
            .pipe(
                switchMap((mapFieldPoint: MapFieldPoint) => {
                    if (requestedProjectId !== this.currentProject.id) {
                        return EMPTY;
                    }
                    return of(mapFieldPoint);
                }),
                finalize(() => {
                    this._getMapFieldPointByProjectRequestKeys.delete(requestKey);
                })
            )
            .subscribe({
                next: (mapFieldPoint: MapFieldPoint) => {
                    if (mapFieldPoint) {
                        this.loadFieldPointToModel(mapFieldPoint);
                    }
                    else {
                        this.initModel();
                        this.addDefaultFieldPointRow(this.model.formArray, this._formBuilder, this.model.form);
                        this.populatePreviewCategories();
                    }

                    this._mapFieldPointReady.next(true);
                },
                error: () => {
                    this._matSnackBar.open(SNACKBAR_CONSTANTS.FIELD_POINT_RETRIEVE_ERROR, SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }
            });
    }

    updateMapFieldPoint(currentApp: App, fieldPointToUpdate: MapFieldPoint): void {
        this._mapDataService.updateMapFieldPoint(currentApp, fieldPointToUpdate).subscribe({
            next: (updatedFieldPoint: MapFieldPoint) => {
                this.loadFieldPointToModel(updatedFieldPoint);
                this._mapFieldPointReady.next(true);
                this._matSnackBar.open(SNACKBAR_CONSTANTS.FIELD_POINT_UPDATED, SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
            },
            error: () => {
                this._matSnackBar.open(SNACKBAR_CONSTANTS.FIELD_POINT_UPDATE_ERROR, SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
            }
        });
    }

    loadFieldPointToModel(fieldPoint: MapFieldPoint): void {
        let pointCategoryArray: UntypedFormArray = null;

        if (!fieldPoint.columns || fieldPoint.columns.length === 0) {
            this.addDefaultFieldPointRow(this.model.form.get('columns') as UntypedFormArray, this._formBuilder, this.model.form);
            return;
        }
        this.sortColumnsByOrder(fieldPoint);
        const columnsFormArray = this._formBuilder.array(
            (fieldPoint.columns || []).map(col => {

                if (col.type === MapFieldPointDefaultFieldTypes.PointCategory) {
                    pointCategoryArray = this._formBuilder.array(
                        (col.pointCategories || []).map(pc =>
                            this._formBuilder.group({
                                id: pc.id || UUID(),
                                name: [pc.name],
                                matIcon: [pc.matIcon],
                                fillColor: [pc.fillColor]
                            })
                        )
                    );
                }

                return this._formBuilder.group({
                    id: [col.id],
                    header: [col.header],
                    type: [col.type],
                    order: [col.order],
                    ...((col.type === GridFieldTypes.Number || col.type === GridFieldTypes.Formula) && {
                        format: col.format || '',
                        decimalPlaces: col.decimalPlaces ?? NUMBER_FORMAT_CONSTANTS.defaultDecimalPlace
                    }),
                    enum: col.type === GridFieldTypes.DropDown ? this._formBuilder.array(col.enum || []) : null,
                    ...(col.type === MapFieldPointDefaultFieldTypes.PointCategory && { pointCategories: pointCategoryArray }),
                    ...(col.type === GridFieldTypes.Formula && {
                        description: col.description || '',
                        richTextFormula: col.richTextFormula || ''
                    }),

                });
            })
        );

        if (!this.model) {
            this.initModel();
        }

        this.model.form = this._formBuilder.group({
            id: [fieldPoint.id],
            columns: columnsFormArray,
            enableSubRow: [fieldPoint?.enableSubRow || false]
        });
        this.model.mapFieldPointSubmitted = false;
        this.model.formArray = columnsFormArray;
        this.model.mapFieldPoint = fieldPoint;
        this.populatePreviewCategories();
    }

    sortColumnsByOrder(fieldPoint: MapFieldPoint): void {
        fieldPoint.columns = fieldPoint.columns.sort((a, b) => (a.order > b.order) ? 1 : (a.order === b.order) ? 0 : -1);
    }

    addDefaultFieldPointRow(formArray: UntypedFormArray, formBuilder: UntypedFormBuilder, formGroup: UntypedFormGroup): void {
        this.defaultColumns.forEach(col => {
            formArray.push(formBuilder.group({
                id: UUID(),
                order: [{ value: col.order, disabled: false }, [Validators.required]],
                header: [{ value: col.header, disabled: false }, [Validators.required]],
                type: [{ value: col.type, disabled: false }, [Validators.required]],
            }));
        });

        formArray.controls.forEach(column => {
            this.prefixFieldType(column as UntypedFormGroup);
        });
        formGroup.setControl('columns', formArray);
        this.model.mapFieldPoint = formGroup.value;
        this.model.form = formGroup;
        this.model.formArray = formArray;
    }

    prefixFieldType(formGroup: UntypedFormGroup): void {
        if (formGroup.value?.type === GridFieldTypes.Text) {
            const idControl = formGroup.get('id');
            if (idControl && !idControl.value.includes(WafPrefix.RichText)) {
                idControl.setValue(`${WafPrefix.RichText}${String(idControl.value)}`);
            }
        }
    }

    noChangesMadeMessage(): void {
        this._matSnackBar.open(SNACKBAR_CONSTANTS.NO_CHANGES_TO_SAVE, SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
    }

    isPointCategoryExists(): boolean {
        const columnsArray = this.model.formArray;
        if (!columnsArray || columnsArray.length === 0) {
            return;
        }

        return columnsArray.controls.some((column: AbstractControl) => {
            const pointCategories = column.get('pointCategories') as UntypedFormArray;
            return pointCategories && pointCategories.length > 0;
        });
    }

    buildTempFormArray(formArray: UntypedFormArray): UntypedFormArray {
        const tempFormArray = this._formBuilder.array([]);

        if (formArray && formArray.length > 0) {
            formArray.controls.forEach(control => {
                const pointCategoryGroup = this._formBuilder.group({
                    id: [control.value.id],
                    name: [control.value.name, [Validators.required]],
                    matIcon: [control.value.matIcon, [Validators.required]],
                    fillColor: [control.value.fillColor, [Validators.required]]
                });
                tempFormArray.push(pointCategoryGroup);
            });
        }
        return tempFormArray;
    }

    setRowIdCreated(rowId: string): void {
        this.model.rowCreatedId = rowId;
    }

    setFieldPointData(data: any): void {
        this.model.fieldPointData = data;
    }

    mapFieldPointRowEdited(): void {
        this._mapFieldPointRowDataEdited.next(true);
    }

    mapFieldPointDataEdited(): void {
        this._mapFieldPointDataEdited.next(true);
    }

    setFieldPointLogicBuilderProperties(fieldPointStyles: LogicStyledGridRowCellDictionary, settableFieldsInGrid: FieldIdentifier[]): void {
        this.model.fieldPointStyles = fieldPointStyles ?? new LogicStyledGridRowCellDictionary();
        this.model.fieldPointSettableFields = settableFieldsInGrid ?? [];
    }

    getFieldPointMarkerPreviewData(fixedCenter: google.maps.LatLng): MapFieldPointMarkerData[] {
        const pointCategoryColumn = this.model.previewPointCategories || [];

        const radiusMiles = .025;
        const milesPerDegLat = 69.0;
        const latRadiusDeg = radiusMiles / milesPerDegLat;
        const lngRadiusDeg = latRadiusDeg / Math.cos(fixedCenter.lat() * Math.PI / 180);
        const angleIncrement = (2 * Math.PI) / pointCategoryColumn.length;

        return pointCategoryColumn.map((cat, idx) => {
            let lat = fixedCenter.lat();
            let lng = fixedCenter.lng();

            if (pointCategoryColumn.length > 1) {
                const angle = idx * angleIncrement;
                lat = fixedCenter.lat() + latRadiusDeg * Math.cos(angle);
                lng = fixedCenter.lng() + lngRadiusDeg * Math.sin(angle);
            }

            return {
                rowId: `preview-${cat.id}`,
                category: cat,
                position: new google.maps.LatLng(lat, lng)
            };
        });
    }

    populatePreviewCategories(): void {
        this.model.previewPointCategories = [];

        if (!this.model?.formArray) {
            return;
        }

        const pointCategoriesColumnForm = (this.model.formArray.value as any[])
            .find(value => value.type === MapFieldPointDefaultFieldTypes.PointCategory);

        if (!pointCategoriesColumnForm.pointCategories) {
            this._pointCategoryPreviewReady.next();
            return;
        }

        pointCategoriesColumnForm.pointCategories.forEach((category) => {
            if (!category) {
                return;
            }
            const pointCategory = new PointCategory();
            pointCategory.name = category.name;
            pointCategory.fillColor = category.fillColor;
            pointCategory.id = category.id;
            pointCategory.matIcon = category.matIcon;

            this.model.previewPointCategories.push(pointCategory);
        });
        this._pointCategoryPreviewReady.next();
    }

    createFieldCategoryMarkers(
        markerContainer: ViewContainerRef,
        map: google.maps.Map,
        markerData: MapFieldPointMarkerData[],
        shouldMarkerBeClustered: boolean
    ): void {
        this.model.fieldPointCluster = this._mapClusterUtilityService.createMarkerCluster(map);
        const markersToCluster: google.maps.marker.AdvancedMarkerElement[] = [];

        markerData.forEach(({ rowId, category, position }, index) => {
            const isMarkerBeingEdited = this.model.fieldPointData?.['id'] === rowId;

            const { element, compRef } = this._htmlRendererUtilityService.createComponentElement(
                FieldPointIconComponent,
                markerContainer,
                {
                    identifier: rowId,
                    isHighlighted: isMarkerBeingEdited,
                    pointCategory: category,
                    size: FieldPointIconsSize.Medium
                }
            );
            this.model.categoryMarkerComponentRefs.push(compRef);

            const marker = new google.maps.marker.AdvancedMarkerElement({
                map: shouldMarkerBeClustered ? null : map,
                position: position,
                content: element,
                //when in edit mode and overlapping other field points, since there is no clustering, this puts it at the highest level under the site marker
                zIndex: isMarkerBeingEdited ? MAP_LAYER_Z_INDEXES.Marker - 1 : MAP_LAYER_Z_INDEXES.FieldPoint + (markerData.length - index),
                gmpDraggable: (isMarkerBeingEdited && this.model.isEditingFieldPointCoordinates),
                gmpClickable: true,
            });

            if (isMarkerBeingEdited) {
                this.model.markerBeingEdited = marker;
            }

            this.model.categoryMarkers.push(marker);
            markersToCluster.push(marker);
        });

        if (this.model.fieldPointCluster && shouldMarkerBeClustered) {
            this.model.fieldPointCluster.addMarkers(markersToCluster);
        }

        this._mapFieldPointsAddedToMap.next(true);
    }

    clearCategoryMarkers(markerContainer: ViewContainerRef): void {
        this.model.categoryMarkers.forEach(m => m.map = null);
        this.model.categoryMarkers = [];
        this.model.categoryMarkerComponentRefs
            .forEach(r => {
                r.destroy();
            });
        this.model.categoryMarkerComponentRefs = [];
        markerContainer.clear();

        if (this.model.fieldPointCluster) {
            this.model.fieldPointCluster.clearMarkers();
            this.model.fieldPointCluster = null;
        }
    }

    private getCategoryById(pointCatId: string): PointCategory | null {
        if (!this.model?.mapFieldPoint) {
            return null;
        }
        return this.model.mapFieldPoint.columns.find((column: Column) => column.pointCategories).pointCategories.find(cat => cat.id === pointCatId) || null;
    }

    getFieldPointMarkersForSite(site: Site): MapFieldPointMarkerData[] {
        if (!site?.data || !this.model?.mapFieldPoint?.columns) {
            return [];
        }

        const columns = this.model.mapFieldPoint.columns;
        const categoryColumn = columns.find(col => col.type === MapFieldPointDefaultFieldTypes.PointCategory);
        const coordinatesColumn = columns.find(col => col.type === MapFieldPointDefaultFieldTypes.Coordinates);

        if (!categoryColumn?.id || !coordinatesColumn?.id) {
            return [];
        }

        const fieldPointRecords = Object.values(site.data)
            .filter(Array.isArray)
            .reduce((mergedFieldPoints, filteredFieldPoints) => mergedFieldPoints.concat(filteredFieldPoints), []) as MapFieldPointMarkerData[];

        return fieldPointRecords
            .map(record => {
                const rowId = record['id'];
                const categoryId = record[categoryColumn.id];
                const coordinatesValue = record[coordinatesColumn.id];

                if (!categoryId) {
                    return null;
                }

                const category = this.getCategoryById(categoryId);
                if (!category) {
                    return null;
                }

                const [latStr, lngStr] = coordinatesValue.split(',').map(c => c.trim());
                const lat = parseFloat(latStr);
                const lng = parseFloat(lngStr);

                if (isNaN(lat) || isNaN(lng)) {
                    return null;
                }

                return {
                    rowId,
                    category,
                    position: new google.maps.LatLng(lat, lng)
                };
            })
            .filter(Boolean);
    }

    setEditingCoordinates(isEditing: boolean): void {
        this.model.isEditingFieldPointCoordinates = isEditing;
        this._editingFieldPointCoordinates.next(isEditing);
    }

    createNewPinBeingEdited(markerContainer: ViewContainerRef, map: google.maps.Map,): void {
        const categoryColumn = this.model.mapFieldPoint.columns.find(col => col.type === MapFieldPointDefaultFieldTypes.PointCategory);
        const { element, compRef } = this._htmlRendererUtilityService.createComponentElement(
            FieldPointIconComponent,
            markerContainer,
            {
                isHighlighted: true,
                pointCategory: this.model.fieldPointData ? this.getCategoryById(this.model.fieldPointData[categoryColumn.id]) : null,
                size: FieldPointIconsSize.Medium
            }
        );
        this.model.categoryMarkerComponentRefs.push(compRef);

        const marker = new google.maps.marker.AdvancedMarkerElement({
            map: map,
            position: null,
            content: element,
            zIndex: MAP_LAYER_Z_INDEXES.FieldPoint + (this.model.categoryMarkerComponentRefs.length - 1),
            gmpDraggable: true,
        });

        this.model.markerBeingEdited = marker;

        this.model.categoryMarkers.push(marker);
    }

    mapFieldPointSiteClusterData(mapFieldPoint: MapFieldPoint, mapFieldPointSiteData: any): FieldPointSiteClusterData[] {
        const pointCategoryColumn = mapFieldPoint?.columns?.find((col) => col.pointCategories);
        const identifierColumn = mapFieldPoint?.columns?.find((col) => col.order === 1);

        const fieldPointSiteClusterData: FieldPointSiteClusterData[] = [];

        if (Array.isArray(mapFieldPointSiteData)) {
            mapFieldPointSiteData.forEach((item) => {
                const categoryId = item?.[pointCategoryColumn?.id];
                if (categoryId) {
                    const pointCategory = pointCategoryColumn?.pointCategories?.find((category) => category.id === categoryId);
                    const identifierFieldValue = item?.[identifierColumn?.id];

                    fieldPointSiteClusterData.push({
                        identifierColumnId: identifierColumn?.id,
                        identifierColumnValue: identifierFieldValue,
                        pointCategory: pointCategory,
                        selectedFields: this.fieldsFromObject(item),
                        selectedFieldPointSiteData: item
                    } as FieldPointSiteClusterData);
                }
            });
        }

        return fieldPointSiteClusterData;
    }

    selectedFields(cachedSelectedFields: FieldIdentifier[], mapFieldPointSiteData: any): FieldIdentifier[] {
        if (cachedSelectedFields !== null) {
            return cachedSelectedFields;
        }

        if (!mapFieldPointSiteData) {
            cachedSelectedFields = [];
            return cachedSelectedFields;
        }

        let dataToProcess: any;

        if (Array.isArray(mapFieldPointSiteData)) {
            dataToProcess = mapFieldPointSiteData[0] || {};
        }
        else {
            dataToProcess = mapFieldPointSiteData;
        }

        return cachedSelectedFields = this.fieldsFromObject(dataToProcess);
    }

    fieldsFromObject(mapFieldPointSiteData: any): FieldIdentifier[] {
        return Object.keys(mapFieldPointSiteData)
            .filter(key => key !== 'id')
            .map(fieldId => ({
                fieldId: fieldId
            } as FieldIdentifier));
    }

    applyVisibilityToPoints(): void {
        if (this.model.fieldPointCluster) {
            this.model.fieldPointCluster.clearMarkers();
        }

        const visibleMarkers: google.maps.marker.AdvancedMarkerElement[] = [];

        this.model.categoryMarkerComponentRefs.forEach(ref => {
            const isVisible = !this.model.hiddenCategoryIds.has(ref.instance.pointCategory.id);
            const marker = this.model.categoryMarkers.find(m => m.content.firstChild === ref.location.nativeElement);
            if (marker && isVisible) {
                visibleMarkers.push(marker);
            }
        });
        if (this.model.fieldPointCluster && visibleMarkers.length > 0) {
            this.model.fieldPointCluster.addMarkers(visibleMarkers);
        }
    }

    setMarkerBeingPreviewed(rowIdentifier: string, marker: google.maps.marker.AdvancedMarkerElement): void {
        this.model.categoryMarkerBeingPreviewed = marker;
        this.fieldPreviewChanged(rowIdentifier);
    }

    fieldPreviewChanged(rowIdentifier: string): void {
        this._fieldPointPreviewedChanged.next(rowIdentifier);
    }

    clearPreviewFieldPoint(): void {
        this.clearPreviewFieldPointData();
        this.model.categoryMarkerBeingPreviewed = null;
        this._fieldPointPreviewedChanged.next(null);
    }

    setHideInfoWindowTimeout(infoWindow: google.maps.InfoWindow): void {
        this._hideInfoWindowTimeout = setTimeout(() => {
            if (!infoWindow.isOpen || this._hideInfoWindowTimeout === null) {
                return;
            }
            infoWindow.close();
        }, this._hideInfoWindowDelay);
    }

    clearHideInfoWindowTimeout(): void {
        if (this._hideInfoWindowTimeout) {
            clearTimeout(this._hideInfoWindowTimeout);
            this._hideInfoWindowTimeout = null;
        }
    }

    setPreviewFieldPointData(data: SiteData[]): void {
        this.model.previewFieldPointData = data;
    }

    fieldPointModalOpened(): void {
        this._fieldPointModalOpened.next();
    }

    private clearPreviewFieldPointData(): void {
        this.model.previewFieldPointData = [];
    }

    viewAllFieldPoints(): void {
        this._viewAllFieldPoints.next();
    }

    editFieldPoint(id: any): void {
        this.model.rowToEditId = id;
        this._editFieldPoint.next();
    }

    clearRowToEditId(): void {
        this.model.rowToEditId = null;
    }

    filterOtherFieldPointInCluster(clusterData: FieldPointSiteClusterData, mapFieldPointSiteData: SiteData[]): void {
        const markersDataToFilterOut = mapFieldPointSiteData.filter(data => data['id'] !== clusterData.selectedFieldPointSiteData['id']);
        markersDataToFilterOut.forEach(data => {
            const markerIndex = this.model.categoryMarkerComponentRefs.findIndex(ref => ref.instance.identifier === data['id']);
            const marker = this.model.categoryMarkers[markerIndex];

            this.model.fieldPointCluster.removeMarker(marker);
        });
        const markerIndex = this.model.categoryMarkerComponentRefs.findIndex(ref => ref.instance.identifier === clusterData.selectedFieldPointSiteData.id);
        const marker = this.model.categoryMarkers[markerIndex];

        this.setMarkerBeingPreviewed(clusterData.selectedFieldPointSiteData.id, marker);
        this.model.areMarkersFilteredInCluster = true;
    }

    updatePointCategoriesComponentCache(currentCache: PointCategory[]): PointCategoryCache {
        const pointCategories = this.model.mapFieldPoint?.columns?.find(col => col.pointCategories)?.pointCategories ?? [];

        if (currentCache === pointCategories) {
            return {
                cache: currentCache, pointCategoriesById: new Map(currentCache.map(pc => [pc.id, pc]))
            };
        }

        return {
            cache: pointCategories,
            pointCategoriesById: new Map(pointCategories.map(pointCategory => [pointCategory.id, pointCategory]))
        };
    }
}
